import { useEffect, useRef, useState } from 'react';
// the styles are imported by main.tsx after decor.css, so they win over it

const MAX_LEAN = 7; // degrees

/**
 * One pointer listener for the whole app: an element marked `data-lean` turns a little towards the pointer while it
 * is over it (CSS reads --lx / --ly). `data-lean="vars"` only receives the angles, for parts that place them inside
 * their own 3D transform. Touch and reduced motion get no lean.
 */
export function useLean() {
  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    let current: HTMLElement | null = null;
    const reset = (el: HTMLElement | null) => {
      el?.style.removeProperty('--lx');
      el?.style.removeProperty('--ly');
    };
    const move = (e: PointerEvent) => {
      if (e.pointerType === 'touch' || reduced.matches) return;
      const el = (e.target as Element | null)?.closest?.('[data-lean]') as HTMLElement | null;
      if (el !== current) {
        reset(current);
        current = el;
      }
      if (!el) return;
      const r = el.getBoundingClientRect();
      const dx = (e.clientX - r.left) / r.width - 0.5;
      const dy = (e.clientY - r.top) / r.height - 0.5;
      el.style.setProperty('--ly', `${(dx * 2 * MAX_LEAN).toFixed(2)}deg`);
      el.style.setProperty('--lx', `${(-dy * 2 * MAX_LEAN).toFixed(2)}deg`);
    };
    const leave = () => {
      reset(current);
      current = null;
    };
    document.addEventListener('pointermove', move, { passive: true });
    document.documentElement.addEventListener('pointerleave', leave);
    return () => {
      document.removeEventListener('pointermove', move);
      document.documentElement.removeEventListener('pointerleave', leave);
    };
  }, []);
}

/**
 * Text whose digits roll like an odometer when they change: a countdown shows time passing without a jolt. The text
 * is split into numbers and the words between them ("1m 10s" → 1 · "m " · 10 · "s"); tokens are keyed from the end
 * and digits within a number from its end, so when a number loses a digit ("10s" → "9s") or the format changes
 * ("1m 0s" → "59s") the units keep their cells and only the digits that changed roll. Digits share one width, so
 * nothing shifts sideways from one second to the next. The old digit slides up and out while the new one slides in
 * from below, on transform and opacity only.
 */
export function RollText({ text }: { text: string }) {
  const tokens = text.match(/\d+|\D+/g) ?? [];
  return (
    <span className="roll" aria-label={text}>
      {tokens.map((tok, i) =>
        /^\d/.test(tok) ? (
          <RollNumber key={tokens.length - i} digits={tok} />
        ) : (
          <span key={tokens.length - i} className="roll-ch roll-text" aria-hidden>
            {tok}
          </span>
        ),
      )}
    </span>
  );
}

function RollNumber({ digits }: { digits: string }) {
  const chars = [...digits];
  return (
    <>
      {chars.map((d, i) => (
        <RollCell key={chars.length - i} ch={d} />
      ))}
    </>
  );
}

function RollCell({ ch }: { ch: string }) {
  const shown = useRef(ch);
  const [change, setChange] = useState<{ from: string; to: string; n: number } | null>(null);
  useEffect(() => {
    if (shown.current === ch) return;
    setChange((c) => ({ from: shown.current, to: ch, n: (c?.n ?? 0) + 1 }));
    shown.current = ch;
  }, [ch]);
  if (!change || change.to !== ch) {
    return (
      <span className="roll-ch roll-digit" aria-hidden>
        {ch}
      </span>
    );
  }
  return (
    <span className="roll-ch roll-digit" aria-hidden>
      <span key={change.n} className="roll-in">
        {ch}
      </span>
      <span key={`out-${change.n}`} className="roll-out">
        {change.from}
      </span>
    </span>
  );
}
